import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Mail, RefreshCw, LogOut, Clock } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

const SUPPORT_EMAIL =
  (import.meta.env.VITE_SUPPORT_EMAIL as string | undefined) || "support@lysara.io";

interface PendingInfo {
  tenantName: string | null;
  tenantSlug: string | null;
  requestStatus: string | null;
  createdAt: string | null;
}

export function PendingActivationScreen() {
  const { user, signOut } = useAuth();
  const queryClient = useQueryClient();
  const [info, setInfo] = useState<PendingInfo>({
    tenantName: null,
    tenantSlug: null,
    requestStatus: null,
    createdAt: null,
  });
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      const { data } = await supabase
        .from("tenant_activation_requests")
        .select("status, created_at, business_name, requested_slug, tenant:tenants(name, slug)")
        .eq("requesting_user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (data) {
        const t: any = data.tenant;
        setInfo({
          tenantName: t?.name ?? data.business_name ?? null,
          tenantSlug: t?.slug ?? data.requested_slug ?? null,
          requestStatus: data.status,
          createdAt: data.created_at,
        });
      }
    })();
  }, [user?.id]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await supabase.auth.refreshSession();
      await queryClient.invalidateQueries();
      toast.success("Status refreshed");
      // Small delay so the tenant context reloads before we potentially unmount
      setTimeout(() => window.location.reload(), 400);
    } catch (e) {
      toast.error("Could not refresh status");
    } finally {
      setRefreshing(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    window.location.href = "/auth";
  };

  const subject = encodeURIComponent(
    `Account activation request — ${info.tenantSlug ?? "new signup"}`,
  );
  const body = encodeURIComponent(
    [
      "Hello Lysara team,",
      "",
      "Please activate my account.",
      "",
      `Email: ${user?.email ?? ""}`,
      `Company ID: ${info.tenantSlug ?? ""}`,
      `Business name: ${info.tenantName ?? ""}`,
      "",
      "Thank you.",
    ].join("\n"),
  );

  const isRejected = info.requestStatus === "rejected";

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Clock className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">
            {isRejected ? "Account activation was declined" : "Account pending activation"}
          </CardTitle>
          <CardDescription>
            {isRejected
              ? "Your activation request was not approved. Please contact us for details."
              : "Your account has been created. A Lysara operator will activate it shortly."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border bg-muted/40 p-4 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Signed in as</span>
              <span className="font-medium">{user?.email}</span>
            </div>
            {info.tenantName && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Business</span>
                <span className="font-medium">{info.tenantName}</span>
              </div>
            )}
            {info.tenantSlug && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Company ID</span>
                <span className="font-mono">{info.tenantSlug}</span>
              </div>
            )}
          </div>

          <p className="text-sm text-muted-foreground">
            To activate your account, please email our team. We'll confirm your details and
            enable your login as soon as possible.
          </p>

          <a href={`mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`} className="block">
            <Button className="w-full" size="lg">
              <Mail className="mr-2 h-4 w-4" />
              Email {SUPPORT_EMAIL}
            </Button>
          </a>

          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              Refresh status
            </Button>
            <Button variant="ghost" onClick={handleSignOut}>
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default PendingActivationScreen;
